import { convert, LocalDate, LocalTime } from '@js-joda/core';
import { noop } from 'lodash';

import {
   AppointmentPractitioner,
   AppointmentRecurrenceDaysParameters,
   AppointmentRecurrenceMonthsParameters,
   AppointmentRecurrenceParametersUnion,
   AppointmentRecurrenceWeeksParameters,
} from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';
import { Choice } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { AppointmentFormBean } from './AppointmentForm.types';
import { PatientValue } from './fields/patient/PatientAutosuggest';

export const parseToDateWithMidnightAdjustment = (
   timeRange: 'START' | 'END',
   date: LocalDate,
   time: LocalTime
): Date => {
   if (timeRange === 'END' && LocalTime.MIDNIGHT.equals(time)) {
      return convert(LocalDate.from(date.atTime(time)).plusDays(1).atStartOfDay()).toDate();
   }

   return convert(LocalDate.parse(date.toString()).atTime(time)).toDate();
};

export const isAvailabilityRangeTimeValid = (date: LocalDate, startTime: LocalTime, endTime: LocalTime): boolean => {
   return date && (startTime.isBefore(endTime) || LocalTime.MIDNIGHT.equals(endTime));
};

export const verifyModeVisitor = (appointmentId?: number) => <ACCEPT>(mode: {
   edition?: () => ACCEPT;
   creation?: () => ACCEPT;
}) => {
   if (appointmentId) {
      return mode.edition?.();
   }

   return mode.creation?.();
};

export const practitionerViewVisitor = (practitioners: AppointmentPractitioner[]) => <ACCEPT>(view: {
   single: () => ACCEPT;
   multiple: () => ACCEPT;
}) => {
   if (practitioners.length === 1 || !protectFeature('searchingAllAppointments')) {
      return view.single();
   }

   return view.multiple();
};

export const patientCreateAvailable = (patient: PatientValue, patientCreateAllowed: boolean | undefined): boolean => {
   return Boolean(!patient && patientCreateAllowed);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const healthcareProvidingLocationViewVisitor = (
   healthcareProviderLocations: HealthcareProviderLocation[],
   selectedHealthcareProviderLocation: HealthcareProviderLocation | null,
   touched: boolean | undefined
) => <ACCEPT>(view: { text: (options: { editable: boolean }) => ACCEPT; select: () => ACCEPT }) => {
   if (!selectedHealthcareProviderLocation || touched) {
      return view.select();
   }

   return view.text({
      editable: healthcareProviderLocations.length > 1,
   });
};

export const resolveRecurrenceParameters = (bean: AppointmentFormBean): AppointmentRecurrenceParametersUnion => {
   const { recurrencePeriodUnit } = bean;

   switch (recurrencePeriodUnit) {
      case 'DAYS': {
         const appointmentParameters = new AppointmentRecurrenceDaysParameters();

         appointmentParameters.overlappingStrategy = Choice.withUseDefaultIfNull(bean.overlappingStrategy ?? null);
         appointmentParameters.recurrencePeriodInDays = Number(bean.recurrencePeriod);
         appointmentParameters.totalNumberOfAppointments = Number(bean.totalNumberOfAppointments);

         return appointmentParameters;
      }
      case 'WEEKS': {
         const appointmentParameters = new AppointmentRecurrenceWeeksParameters();

         appointmentParameters.overlappingStrategy = Choice.withUseDefaultIfNull(bean.overlappingStrategy ?? null);
         appointmentParameters.recurrencePeriodInWeeks = Number(bean.recurrencePeriod);
         appointmentParameters.totalNumberOfAppointments = Number(bean.totalNumberOfAppointments);
         appointmentParameters.daysOfWeek = bean.daysOfWeek!;

         return appointmentParameters;
      }
      case 'MONTHS': {
         const appointmentParameters = new AppointmentRecurrenceMonthsParameters();

         appointmentParameters.overlappingStrategy = Choice.withUseDefaultIfNull(bean.overlappingStrategy ?? null);
         appointmentParameters.recurrencePeriodInMonths = Number(bean.recurrencePeriod);
         appointmentParameters.totalNumberOfAppointments = Number(bean.totalNumberOfAppointments);
         appointmentParameters.recurrenceType = bean.recurrenceType!;

         return appointmentParameters;
      }
      default:
         return assertUnreachable(recurrencePeriodUnit);
   }
};

export const recurrencePeriodVisitor = (bean: AppointmentFormBean) => <ACCEPT>(visitor: {
   forMonth: () => ACCEPT;
   forWeek: () => ACCEPT;
}) => {
   const { recurrencePeriodUnit } = bean;

   switch (recurrencePeriodUnit) {
      case 'DAYS':
         return null;
      case noop():
      case 'WEEKS':
         return visitor.forWeek();
      case 'MONTHS':
         return visitor.forMonth();
      default:
         return assertUnreachable(recurrencePeriodUnit);
   }
};

export const getPractitionerName = (practitioner: AppointmentPractitioner): string =>
   practitioner ? `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}` : '';
